import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import SaveIcon from '@mui/icons-material/Save';
import { useHistory, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { AbilityContext } from '../../config/ability-context';
import DialogConfirmation from '../../components/dialog-confirmation-not-button';
import EmailSettingsLayout from '../../components/email-settings-layout';
import {
  EmailSettingsEmptyState,
  EmailSettingsFormSkeleton,
  EmailSettingsPageHeader,
  EmailSettingsPanel,
  EmailSettingsStatusChip,
  EmailSettingsTable,
} from '../../components/email-settings-modern';
import * as EmailTemplateService from '../../services/emailtemplate.service';
import * as StatusService from '../../services/status.service';
import * as Service from '../../services/statusemailnotification.service';

/* MOCK TEMPORÃRIO DESATIVADO
const MOCK_STATUSES = [
  { id: 10, name: 'Novo' },
  { id: 20, name: 'Em atendimento' },
  { id: 30, name: 'Aguardando documento' },
  { id: 40, name: 'Resolvido' },
];
*/

const EMPTY_NOTIFICATION = {
  statusid: null,
  templateid: null,
  active: true,
};

const EMPTY_REMINDER = {
  id: null,
  templateid: null,
  waithours: '',
  usebusinessdays: false,
  order: 1,
  active: true,
  eligibilitymode: 'alwayssend',
};

const unwrapData = result => {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.data)) return result.data;
  if (Array.isArray(result?.data?.data)) return result.data.data;
  return [];
};

export default function StatusEmailNotificationForm() {
  const history = useHistory();
  const { lang, notificationId } = useParams();
  const intl = useIntl();
  const ability = useContext(AbilityContext);
  const isEdit = Boolean(notificationId);
  const canView = ability?.can(isEdit ? 'View' : 'Add', 'EmailCustomSettings');
  const canAdd = ability?.can('Add', 'EmailCustomSettings');
  const canEdit = ability?.can('Edit', 'EmailCustomSettings');
  const canDelete = ability?.can('Delete', 'EmailCustomSettings');
  const canSave = isEdit ? canEdit : canAdd;

  const [model, setModel] = useState(EMPTY_NOTIFICATION);
  const [statuses, setStatuses] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [templateSearch, setTemplateSearch] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [remindersLoading, setRemindersLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [busyReminderId, setBusyReminderId] = useState(null);
  const [reminderDialog, setReminderDialog] = useState(false);
  const [reminder, setReminder] = useState(EMPTY_REMINDER);
  const [reminderMenuAnchor, setReminderMenuAnchor] = useState(null);
  const [reminderMenuItem, setReminderMenuItem] = useState(null);
  const [confirmReminder, setConfirmReminder] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    variant: 'success',
    message: '',
  });

  const message = id => intl.formatMessage({ id });
  const showNotification = (variant, text) =>
    setNotification({ open: true, variant, message: text });
  const isBusy =
    actionLoading || templatesLoading || remindersLoading || busyReminderId !== null;

  const loadTemplates = async (search = '') => {
    setTemplatesLoading(true);
    try {
      const result = await EmailTemplateService.list({
        search,
        active: true,
        page: 1,
        pagesize: 100,
      });
      if (result.success) {
        setTemplates(current => {
          const merged = [...result.data];
          current.forEach(item => {
            if (!merged.some(candidate => candidate.id === item.id)) merged.push(item);
          });
          return merged;
        });
      } else {
        showNotification(
          'error',
          result.message || message('emailtemplates.load.error')
        );
      }
    } finally {
      setTemplatesLoading(false);
    }
  };

  const loadReminders = async () => {
    if (!isEdit) return;
    setRemindersLoading(true);
    try {
      const result = await Service.listReminders(notificationId);
      if (result.success) {
        setReminders(result.data);
      } else {
        showNotification(
          'error',
          result.message || message('emailreminders.load.error')
        );
      }
    } finally {
      setRemindersLoading(false);
    }
  };

  useEffect(() => {
    if (!canView) return;
    const load = async () => {
      setInitialLoading(true);
      try {
      const requests = [
        StatusService.getAllSelectionModel(),
        // MOCK TEMPORÃRIO DESATIVADO:
        // Promise.resolve({ success: true, data: MOCK_STATUSES }),
        EmailTemplateService.list({
          active: true,
          page: 1,
          pagesize: 100,
        }),
      ];
      if (isEdit) {
        requests.push(Service.getById(notificationId));
        requests.push(Service.listReminders(notificationId));
      }

      const [statusResult, templateResult, notificationResult, reminderResult] =
        await Promise.all(requests);
      setStatuses(unwrapData(statusResult));

      if (templateResult.success) {
        setTemplates(templateResult.data);
      } else {
        showNotification(
          'error',
          templateResult.message || message('emailtemplates.load.error')
        );
      }

      if (isEdit) {
        if (notificationResult.success && notificationResult.data) {
          const data = notificationResult.data;
          setModel({
            statusid: data.statusid,
            templateid: data.templateid,
            active: Boolean(data.active),
          });
          setTemplates(current => {
            if (current.some(item => item.id === data.templateid)) return current;
            return [
              ...current,
              { id: data.templateid, name: data.templatename, active: true },
            ];
          });
        } else {
          showNotification(
            'error',
            notificationResult.message ||
              message('statusemailnotifications.load.one.error')
          );
        }

        if (reminderResult.success) {
          setReminders(reminderResult.data);
        } else {
          showNotification(
            'error',
            reminderResult.message || message('emailreminders.load.error')
          );
        }
      }
      } finally {
        setInitialLoading(false);
      }
    };
    load();
  }, [canView, isEdit, notificationId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (canView && !initialLoading) loadTemplates(templateSearch.trim());
    }, 350);
    return () => clearTimeout(timeout);
  }, [templateSearch, canView, initialLoading]);

  const selectedStatus = useMemo(
    () => statuses.find(item => item.id === model.statusid) || null,
    [model.statusid, statuses]
  );
  const selectedTemplate = useMemo(
    () => templates.find(item => item.id === model.templateid) || null,
    [model.templateid, templates]
  );
  const selectedReminderTemplate = useMemo(
    () => templates.find(item => item.id === reminder.templateid) || null,
    [reminder.templateid, templates]
  );

  const saveNotification = async () => {
    if (isBusy) return;
    if (!model.statusid) {
      showNotification(
        'error',
        message('statusemailnotifications.validation.status')
      );
      return;
    }
    if (!model.templateid) {
      showNotification(
        'error',
        message('statusemailnotifications.validation.template')
      );
      return;
    }

    setActionLoading(true);
    const payload = {
      statusid: model.statusid,
      templateid: model.templateid,
      active: model.active,
    };
    try {
      const result = isEdit
        ? await Service.update(notificationId, payload)
        : await Service.create(payload);

      if (result.success) {
        showNotification(
          'success',
          message('statusemailnotifications.save.success')
        );
        if (!isEdit && result.data?.id) {
          history.replace(
            `/${lang}/status-email-notification-edit/${result.data.id}`
          );
        }
      } else {
        showNotification(
          'error',
          result.message || message('statusemailnotifications.save.error')
        );
      }
    } finally {
      setActionLoading(false);
    }
  };

  const openNewReminder = () => {
    const nextOrder =
      reminders.reduce((maximum, item) => Math.max(maximum, item.order || 0), 0) + 1;
    setReminder({ ...EMPTY_REMINDER, order: nextOrder });
    setReminderDialog(true);
  };

  const openReminder = row => {
    setReminder({
      id: row.id,
      templateid: row.templateid,
      waithours: row.waithours == null ? '' : String(row.waithours),
      usebusinessdays: Boolean(row.usebusinessdays),
      order: row.order,
      active: Boolean(row.active),
      eligibilitymode: row.eligibilitymode || 'alwayssend',
    });
    setReminderDialog(true);
  };

  const saveReminder = async () => {
    if (isBusy) return;
    const waitHours = Number(String(reminder.waithours).replace(',', '.'));
    const order = Number(reminder.order);
    if (!reminder.templateid) {
      showNotification('error', message('emailreminders.validation.template'));
      return;
    }
    if (!Number.isFinite(waitHours) || waitHours <= 0) {
      showNotification('error', message('emailreminders.validation.hours'));
      return;
    }
    if (!Number.isInteger(order) || order <= 0) {
      showNotification('error', message('emailreminders.validation.order'));
      return;
    }

    setActionLoading(true);
    const payload = {
      templateid: reminder.templateid,
      waithours: waitHours,
      usebusinessdays: reminder.usebusinessdays,
      order,
      active: reminder.active,
    };
    try {
      const result = reminder.id
        ? await Service.updateReminder(notificationId, reminder.id, payload)
        : await Service.createReminder(notificationId, payload);

      if (result.success) {
        setReminderDialog(false);
        showNotification('success', message('emailreminders.save.success'));
        await loadReminders();
      } else {
        showNotification(
          'error',
          result.message || message('emailreminders.save.error')
        );
      }
    } finally {
      setActionLoading(false);
    }
  };

  const toggleReminderActive = async (event, row) => {
    event.stopPropagation();
    if (isBusy) return;
    setBusyReminderId(row.id);
    try {
      const result = await Service.setReminderActive(
        notificationId,
        row.id,
        !row.active
      );
      if (result.success) {
        showNotification('success', message('emailreminders.active.success'));
        await loadReminders();
      } else {
        showNotification(
          'error',
          result.message || message('emailreminders.active.error')
        );
      }
    } finally {
      setBusyReminderId(null);
    }
  };

  const deleteReminder = async () => {
    if (!confirmReminder || isBusy) return;
    const id = confirmReminder.id;
    setConfirmReminder(null);
    setBusyReminderId(id);
    try {
      const result = await Service.deleteReminder(notificationId, id);
      if (result.success) {
        showNotification('success', message('emailreminders.delete.success'));
        await loadReminders();
      } else {
        showNotification(
          'error',
          result.message || message('emailreminders.delete.error')
        );
      }
    } finally {
      setBusyReminderId(null);
    }
  };

  const openReminderMenu = (event, row) => {
    event.stopPropagation();
    setReminderMenuAnchor(event.currentTarget);
    setReminderMenuItem(row);
  };

  const closeReminderMenu = () => {
    setReminderMenuAnchor(null);
    setReminderMenuItem(null);
  };

  const requestDeleteReminder = () => {
    setConfirmReminder(reminderMenuItem);
    closeReminderMenu();
  };

  const reminderColumns = [
    { key: 'order', label: message('emailreminders.order'), width: 90 },
    { key: 'template', label: message('emailreminders.template') },
    { key: 'wait', label: message('emailreminders.wait.hours'), width: 130 },
    { key: 'calendar', label: message('emailreminders.calendar'), width: 180 },
    {
      key: 'eligibility',
      label: message('emailreminders.eligibility'),
      width: 150,
    },
    { key: 'active', label: message('emailsettings.status'), width: 190 },
    { key: 'actions', label: '', width: 60, align: 'right' },
  ];

  if (!canView) {
    return (
      <EmailSettingsLayout>
        <Typography variant="h6">{message('emailsettings.forbidden')}</Typography>
      </EmailSettingsLayout>
    );
  }

  if (initialLoading) {
    return (
      <EmailSettingsLayout
        notification={notification}
        closeNotification={() =>
          setNotification(current => ({ ...current, open: false }))
        }
      >
        <EmailSettingsFormSkeleton sections={isEdit ? 2 : 1} />
      </EmailSettingsLayout>
    );
  }

  return (
    <EmailSettingsLayout
      notification={notification}
      closeNotification={() =>
        setNotification(current => ({ ...current, open: false }))
      }
    >
      <EmailSettingsPageHeader
        icon={<NotificationsActiveOutlinedIcon />}
        title={message(
          isEdit
            ? 'statusemailnotifications.edit.title'
            : 'statusemailnotifications.create.title'
        )}
        subtitle={message('statusemailnotifications.form.subtitle')}
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              disabled={isBusy}
              onClick={() =>
                history.push(`/${lang}/status-email-notifications`)
              }
              sx={{
                textTransform: 'none',
                borderColor: '#cfcfcf',
                color: '#444',
              }}
            >
              {message('emailsettings.back')}
            </Button>
            {canSave && (
              <Button
                variant="contained"
                startIcon={
                  actionLoading ? (
                    <CircularProgress size={17} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
                disabled={isBusy}
                onClick={saveNotification}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#8BC34A',
                  '&:hover': { backgroundColor: '#7cb342' },
                }}
              >
                {message('emailsettings.save')}
              </Button>
            )}
          </>
        }
      />

      <EmailSettingsPanel
        title={message('statusemailnotifications.section.basic')}
        subtitle={message('statusemailnotifications.duplicate.help')}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={5}>
            <Autocomplete
              disabled={!canSave || isBusy}
              loading={templatesLoading}
              options={statuses}
              value={selectedStatus}
              getOptionLabel={option => option.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_event, value) =>
                setModel(current => ({
                  ...current,
                  statusid: value?.id || null,
                }))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  required
                  label={message('statusemailnotifications.status')}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <Autocomplete
              disabled={!canSave || isBusy}
              loading={templatesLoading}
              options={templates}
              value={selectedTemplate}
              getOptionLabel={option => option.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onInputChange={(_event, value, reason) => {
                if (reason === 'input') setTemplateSearch(value);
              }}
              onChange={(_event, value) =>
                setModel(current => ({
                  ...current,
                  templateid: value?.id || null,
                }))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  required
                  label={message('statusemailnotifications.template')}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={model.active}
                  disabled={!canSave || isBusy}
                  color="secondary"
                  onChange={event =>
                    setModel(current => ({
                      ...current,
                      active: event.target.checked,
                    }))
                  }
                />
              }
              label={message('emailsettings.active')}
            />
          </Grid>
        </Grid>
      </EmailSettingsPanel>

      {isEdit && (
        <EmailSettingsPanel
          title={message('emailreminders.title')}
          subtitle={message('emailreminders.help')}
          action={
            canAdd ? (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={isBusy}
                onClick={openNewReminder}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#8BC34A',
                  '&:hover': { backgroundColor: '#7cb342' },
                }}
              >
                {message('emailreminders.add')}
              </Button>
            ) : null
          }
        >
          {remindersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} sx={{ color: '#8BC34A' }} />
            </Box>
          ) : reminders.length > 0 ? (
            <EmailSettingsTable
              columns={reminderColumns}
              rows={reminders}
              minWidth={980}
              onRowClick={canEdit ? (_event, row) => openReminder(row) : undefined}
              renderCells={row => [
                <Chip
                  key="order"
                  size="small"
                  label={`#${row.order}`}
                  sx={{
                    borderRadius: '6px',
                    backgroundColor: '#f1f1f1',
                    fontWeight: 700,
                  }}
                />,
                <Typography
                  key="template"
                  sx={{ fontSize: 13, fontWeight: 600, color: '#277afe' }}
                >
                  {row.templatename}
                </Typography>,
                <Typography key="wait" sx={{ fontSize: 13, fontWeight: 600 }}>
                  {row.waithours}h
                </Typography>,
                <Chip
                  key="calendar"
                  size="small"
                  label={
                    row.usebusinessdays
                      ? message('emailreminders.business.days')
                      : message('emailreminders.elapsed.hours')
                  }
                  sx={{
                    borderRadius: '6px',
                    backgroundColor: row.usebusinessdays ? '#eef7e3' : '#e8f1ff',
                    color: row.usebusinessdays ? '#5b9b1e' : '#277afe',
                    fontWeight: 600,
                  }}
                />,
                <Chip
                  key="eligibility"
                  size="small"
                  label={message('emailreminders.alwayssend')}
                  sx={{
                    borderRadius: '6px',
                    backgroundColor: '#f4ecfa',
                    color: '#8e44ad',
                    fontWeight: 600,
                  }}
                />,
                <Box
                  key="active"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <EmailSettingsStatusChip
                    active={Boolean(row.active)}
                    activeLabel={message('emailsettings.active')}
                    inactiveLabel={message('emailsettings.inactive')}
                  />
                  {busyReminderId === row.id ? (
                    <CircularProgress size={20} sx={{ color: '#8BC34A' }} />
                  ) : (
                    <Switch
                      size="small"
                      checked={Boolean(row.active)}
                      disabled={!canEdit || isBusy}
                      color="secondary"
                      onClick={event => event.stopPropagation()}
                      onChange={event => toggleReminderActive(event, row)}
                    />
                  )}
                </Box>,
                canEdit || canDelete ? (
                  <IconButton
                    key="actions"
                    size="small"
                    disabled={isBusy}
                    onClick={event => openReminderMenu(event, row)}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                ) : null,
              ]}
            />
          ) : (
            <EmailSettingsEmptyState
              icon={<NotificationsActiveOutlinedIcon sx={{ fontSize: 36 }} />}
              title={message('emailreminders.empty.title')}
              description={message('emailreminders.empty.description')}
              actionLabel={canAdd ? message('emailreminders.add') : null}
              onAction={canAdd ? openNewReminder : null}
            />
          )}
        </EmailSettingsPanel>
      )}

      <Dialog
        open={reminderDialog}
        onClose={() => !isBusy && setReminderDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#444' }}>
            {message(
              reminder.id
                ? 'emailreminders.edit.title'
                : 'emailreminders.create.title'
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Chip
              size="small"
              label={message('emailreminders.alwayssend')}
              sx={{ backgroundColor: '#f4ecfa', color: '#8e44ad' }}
            />
            <Chip
              size="small"
              label={
                reminder.usebusinessdays
                  ? message('emailreminders.business.days')
                  : message('emailreminders.elapsed.hours')
              }
              sx={{
                backgroundColor: reminder.usebusinessdays
                  ? '#eef7e3'
                  : '#e8f1ff',
                color: reminder.usebusinessdays ? '#5b9b1e' : '#277afe',
              }}
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            <Grid item xs={12}>
              <Autocomplete
                disabled={isBusy}
                loading={templatesLoading}
                options={templates}
                value={selectedReminderTemplate}
                getOptionLabel={option => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onInputChange={(_event, value, reason) => {
                  if (reason === 'input') setTemplateSearch(value);
                }}
                onChange={(_event, value) =>
                  setReminder(current => ({
                    ...current,
                    templateid: value?.id || null,
                  }))
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    required
                    label={message('emailreminders.template')}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="number"
                disabled={isBusy}
                label={message('emailreminders.wait.hours')}
                value={reminder.waithours}
                inputProps={{ min: 0.01, step: 0.01 }}
                onChange={event =>
                  setReminder(current => ({
                    ...current,
                    waithours: event.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="number"
                disabled={isBusy}
                label={message('emailreminders.order')}
                value={reminder.order}
                inputProps={{ min: 1, step: 1 }}
                onChange={event =>
                  setReminder(current => ({
                    ...current,
                    order: event.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={reminder.usebusinessdays}
                    disabled={isBusy}
                    color="secondary"
                    onChange={event =>
                      setReminder(current => ({
                        ...current,
                        usebusinessdays: event.target.checked,
                      }))
                    }
                  />
                }
                label={message('emailreminders.use.business.days')}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={reminder.active}
                    disabled={isBusy}
                    color="secondary"
                    onChange={event =>
                      setReminder(current => ({
                        ...current,
                        active: event.target.checked,
                      }))
                    }
                  />
                }
                label={message('emailsettings.active')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                disabled
                label={message('emailreminders.eligibility')}
                value={message('emailreminders.alwayssend')}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                {message('emailreminders.business.days.help')}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={isBusy}
            onClick={() => setReminderDialog(false)}
            sx={{ textTransform: 'none' }}
          >
            {message('emailsettings.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={isBusy}
            startIcon={
              actionLoading ? (
                <CircularProgress size={17} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            onClick={saveReminder}
            sx={{
              textTransform: 'none',
              backgroundColor: '#8BC34A',
              '&:hover': { backgroundColor: '#7cb342' },
            }}
          >
            {message('emailsettings.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={reminderMenuAnchor}
        open={Boolean(reminderMenuAnchor)}
        onClose={closeReminderMenu}
      >
        {canEdit && (
          <MenuItem
            disabled={isBusy}
            onClick={() => {
              const item = reminderMenuItem;
              closeReminderMenu();
              if (item) openReminder(item);
            }}
            sx={{ fontSize: 13, gap: 1 }}
          >
            <EditOutlinedIcon fontSize="small" />
            {message('emailsettings.edit')}
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem
            disabled={isBusy}
            onClick={requestDeleteReminder}
            sx={{ fontSize: 13, gap: 1, color: '#c0392b' }}
          >
            <DeleteOutlineIcon fontSize="small" />
            {message('emailsettings.delete')}
          </MenuItem>
        )}
      </Menu>

      {confirmReminder && (
        <DialogConfirmation
          msg={message('emailsettings.delete.confirm.title')}
          optionToDelete={
            <span style={{ fontSize: '0.95rem', fontWeight: 400, color: '#444' }}>
              {message('emailreminders.delete.confirm').replace(
                '{template}',
                confirmReminder.templatename
              )}
            </span>
          }
          msgAgree={message('yes')}
          msgDisagree={message('no')}
          handleClose={() => setConfirmReminder(null)}
          handleConfirmation={deleteReminder}
        />
      )}
    </EmailSettingsLayout>
  );
}
